// ─────────────────────────────────────────────────────────
// Einstellungen → Verbindungen (Gmail + WhatsApp an einem Ort)
// ─────────────────────────────────────────────────────────
// Eigenes Modul, Aktions-Prefix `vb-`. Deutsche UI.
//
// KEIN plancraft-Nachbau: plancrafts „Schnittstellen" ist eine Einstellung für
// Eingangsrechnungen (haben wir nicht). Gmail und WhatsApp sind unsere Features —
// bisher verband man sie mitten in der Arbeitsansicht (Empty-State der E-Mail-Seite,
// QR-Panel der WhatsApp-Seite). Diese Seite ist der zentrale Ort dafür.
//
// Rechte: connections:read zum Ansehen, connections:write zum Verbinden/Koppeln,
// connections:delete zum Trennen/Löschen — dieselben Keys, an denen jetzt auch die
// ändernden WhatsApp-Instanz-Routen im Backend hängen.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
import { icon, escapeHtml } from '../../utils/dom.js';
import { listConnections, deleteConnection, deleteConnectionData } from '../../core/org.js';
import { startOAuthFlow, connectImap } from '../../core/oauth.js';
import {
  listWhatsAppInstances, createWhatsAppInstance, removeWhatsAppInstance,
  connectWhatsApp, getWhatsAppStatus, disconnectWhatsApp, deleteWhatsAppData,
} from '../../core/whatsapp.js';
import { WHATSAPP_ENABLED } from '../../core/features.js';
import { dateTime } from '../../utils/format.ts';
import { t } from '../../i18n/index.js';

// ── Daten laden ───────────────────────────────────────────

/** Beide Quellen parallel; eine kaputte Seite darf die andere nicht verstecken. */
export async function loadConnections(ctx) {
  const { s } = ctx;
  s.vbLoading = true;
  s.vbError = '';
  try {
    // WhatsApp pausiert (core/features.js): die Instanz-Route ist gar nicht
    // eingehängt — sie erst zu fragen und den Fehler zu schlucken wäre Lärm.
    const [conns, insts] = await Promise.all([
      listConnections().catch(() => null),
      WHATSAPP_ENABLED ? listWhatsAppInstances().catch(() => null) : Promise.resolve(null),
    ]);
    s.vbConnections = (conns && conns.connections) || [];
    s.vbInstances = (insts && insts.instances) || [];
    if (!conns && (!insts && WHATSAPP_ENABLED)) s.vbError = t('settings.connections.loadFailed');
  } finally {
    s.vbLoading = false;
  }
}

// ── Helfer ────────────────────────────────────────────────

const fmtDateTime = (iso) => dateTime(iso) || '—';

// whatsapp.service.mapState() normalisiert Evolutions Rohzustände bereits auf
// pending | connecting | connected | disconnected — 'open' kommt hier nie an.
function waState(inst) {
  const st = inst && inst.status;
  if (st === 'connected') return 'connected';
  if (st === 'connecting') return 'connecting';
  return 'disconnected';
}

const badge = (state) =>
  state === 'connected'
    ? `<span class="cd-vb-badge cd-vb-on">${icon('check_circle', 14)}${escapeHtml(t('settings.connections.statusConnected'))}</span>`
    : state === 'connecting'
      ? `<span class="cd-vb-badge cd-vb-off">${icon('sync', 14)}${escapeHtml(t('settings.connections.statusConnecting'))}</span>`
      : `<span class="cd-vb-badge cd-vb-off">${icon('cancel', 14)}${escapeHtml(t('settings.connections.statusDisconnected'))}</span>`;

// ── Gmail-Karte ───────────────────────────────────────────

function gmailCard(ctx) {
  const { s, can } = ctx;
  const list = s.vbConnections || [];
  const google = list.filter((c) => c.provider === 'GOOGLE');
  const canWrite = can('connections:write');
  const canDelete = can('connections:delete');

  const rows = google.length
    ? google
        .map(
          (c) => `
      <div class="cd-vb-row">
        <div class="cd-vb-main">
          <div class="cd-vb-name">${escapeHtml(c.email)}</div>
          <div class="cd-vb-sub">${escapeHtml(t('settings.connections.lastUsed', { date: fmtDateTime(c.lastUsedAt) }))}</div>
        </div>
        ${badge(c.isValid ? 'connected' : 'disconnected')}
        ${canDelete
            ? `<button class="cd-btn-ghost cd-vb-danger" data-action="vb-gmail-remove" data-id="${escapeHtml(c.id)}">${icon('link_off', 15)}${escapeHtml(t('settings.connections.disconnect'))}</button>`
            : ''}
        ${canDelete
            ? `<button class="cd-btn-ghost cd-vb-danger" data-action="vb-gmail-delete-data" data-id="${escapeHtml(c.id)}">${icon('delete_forever', 15)}${escapeHtml(t('settings.connections.gmailDeleteData'))}</button>`
            : ''}
      </div>`,
        )
        .join('')
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.connections.gmailEmpty'))}</div>`;

  return `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('mail', 18)}<h3>Google / Gmail</h3>
        ${canWrite
          ? `<button class="cd-btn-ghost" data-action="vb-gmail-connect" ${s.vbBusy ? 'disabled' : ''}>${icon('add_link', 15)}${escapeHtml(google.length ? t('settings.connections.gmailAddAnother') : t('settings.connections.gmailConnect'))}</button>`
          : ''}
      </div>
      <div class="cd-vb-body">
        <p class="cd-vb-hint">${escapeHtml(t('settings.connections.gmailHint'))}</p>
        ${rows}
      </div>
    </div>`;
}

// ── Microsoft 365 / Outlook ───────────────────────────────
// Derselbe Aufbau wie die Gmail-Karte: verbinden per OAuth-Popup, kein
// Passwort bei uns. Eigene Karte statt einer Zeile in der Gmail-Karte, weil
// es ein eigener Anbieter mit eigener Freigabe ist — und weil ein Betrieb
// beides haben darf.
function microsoftCard(ctx) {
  const { s, can } = ctx;
  const list = (s.vbConnections || []).filter((c) => c.provider === 'MICROSOFT');
  const canWrite = can('connections:write');
  const canDelete = can('connections:delete');

  const rows = list.length
    ? list.map((c) => `
      <div class="cd-vb-row">
        <div class="cd-vb-main">
          <div class="cd-vb-name">${escapeHtml(c.email)}</div>
          <div class="cd-vb-sub">${escapeHtml(t('settings.connections.lastUsed', { date: fmtDateTime(c.lastUsedAt) }))}</div>
        </div>
        ${badge(c.isValid ? 'connected' : 'disconnected')}
        ${canDelete
          ? `<button class="cd-btn-ghost cd-vb-danger" data-action="vb-ms-remove" data-id="${escapeHtml(c.id)}">${icon('link_off', 15)}${escapeHtml(t('settings.connections.disconnect'))}</button>`
          : ''}
      </div>`).join('')
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.connections.msEmpty'))}</div>`;

  return `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('mail', 18)}<h3>Microsoft 365 / Outlook</h3>
        ${canWrite
          ? `<button class="cd-btn-ghost" data-action="vb-ms-connect" ${s.vbBusy ? 'disabled' : ''}>${icon('add_link', 15)}${escapeHtml(list.length ? t('settings.connections.gmailAddAnother') : t('settings.connections.gmailConnect'))}</button>`
          : ''}
      </div>
      <div class="cd-vb-body">
        <p class="cd-vb-hint">${escapeHtml(t('settings.connections.msHint'))}</p>
        ${rows}
      </div>
    </div>`;
}

// ── Eigenes Postfach (IMAP/SMTP) ──────────────────────────
// Für Betriebe ohne Gmail (info@firma.ch bei Hostpoint, Infomaniak …). Die
// Felder sind dieselben wie im Onboarding-Schritt — deshalb auch dieselben
// Übersetzungen (onboarding.email.*), statt sie ein zweites Mal zu pflegen.
function imapCard(ctx) {
  const { s, can } = ctx;
  const list = (s.vbConnections || []).filter((c) => c.provider === 'IMAP');
  const canWrite = can('connections:write');
  const canDelete = can('connections:delete');
  const f = s.vbImapForm || {};

  const rows = list.length
    ? list.map((c) => `
      <div class="cd-vb-row">
        <div class="cd-vb-main">
          <div class="cd-vb-name">${escapeHtml(c.email)}</div>
          <div class="cd-vb-sub">${escapeHtml(t('settings.connections.lastUsed', { date: fmtDateTime(c.lastUsedAt) }))}</div>
        </div>
        ${badge(c.isValid ? 'connected' : 'disconnected')}
        ${canDelete
          ? `<button class="cd-btn-ghost cd-vb-danger" data-action="vb-imap-remove" data-id="${escapeHtml(c.id)}">${icon('link_off', 15)}${escapeHtml(t('settings.connections.disconnect'))}</button>`
          : ''}
      </div>`).join('')
    : '';

  const form = s.vbImapOpen && canWrite ? `
    <div class="cd-vb-imap">
      ${s.vbImapError ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(s.vbImapError)}</span></div>` : ''}
      <div class="cd-dt-field">
        <label class="cd-dt-cap" for="vb-imap-email">${escapeHtml(t('onboarding.email.address'))}</label>
        <input class="cd-input" id="vb-imap-email" type="email" autocomplete="off" value="${escapeHtml(f.email || '')}">
      </div>
      <div class="cd-dt-field">
        <label class="cd-dt-cap" for="vb-imap-pass">${escapeHtml(t('settings.account.current'))}</label>
        <input class="cd-input" id="vb-imap-pass" type="password" autocomplete="new-password" value="">
      </div>
      <div class="cd-vb-imap-grid">
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="vb-imap-host">${escapeHtml(t('onboarding.email.imapServer'))}</label>
          <input class="cd-input" id="vb-imap-host" type="text" value="${escapeHtml(f.imapHost || '')}">
        </div>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="vb-imap-port">${escapeHtml(t('onboarding.email.imapPort'))}</label>
          <input class="cd-input" id="vb-imap-port" type="number" min="1" max="65535" value="${escapeHtml(String(f.imapPort || 993))}">
        </div>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="vb-smtp-host">${escapeHtml(t('onboarding.email.smtpServer'))}</label>
          <input class="cd-input" id="vb-smtp-host" type="text" value="${escapeHtml(f.smtpHost || '')}">
        </div>
        <div class="cd-dt-field">
          <label class="cd-dt-cap" for="vb-smtp-port">${escapeHtml(t('onboarding.email.smtpPort'))}</label>
          <input class="cd-input" id="vb-smtp-port" type="number" min="1" max="65535" value="${escapeHtml(String(f.smtpPort || 465))}">
        </div>
      </div>
      <div class="cd-dt-field">
        <label class="cd-dt-cap" for="vb-imap-sec">${escapeHtml(t('onboarding.email.security'))}</label>
        <div class="cd-select-shell">
          <select class="cd-input" id="vb-imap-sec">
            <option value="SSL" ${f.security !== 'STARTTLS' ? 'selected' : ''}>${escapeHtml(t('onboarding.email.sslTls'))}</option>
            <option value="STARTTLS" ${f.security === 'STARTTLS' ? 'selected' : ''}>${escapeHtml(t('onboarding.email.starttls'))}</option>
          </select>${icon('expand_more', 18)}
        </div>
      </div>
      <p class="cd-vb-hint">${escapeHtml(t('onboarding.email.imapHint'))}</p>
      <div class="cd-vb-imap-actions">
        <button class="cd-btn-ghost" data-action="vb-imap-cancel" ${s.vbImapBusy ? 'disabled' : ''}>${escapeHtml(t('common.cancel'))}</button>
        <button class="cd-btn-primary" data-action="vb-imap-save" ${s.vbImapBusy ? 'disabled' : ''}>${escapeHtml(s.vbImapBusy ? t('onboarding.email.connecting') : t('onboarding.email.testConnect'))}</button>
      </div>
    </div>` : '';

  return `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('alternate_email', 18)}<h3>${escapeHtml(t('onboarding.email.imapTitle'))}</h3>
        ${canWrite && !s.vbImapOpen
          ? `<button class="cd-btn-ghost" data-action="vb-imap-open">${icon('add_link', 15)}${escapeHtml(t('settings.connections.gmailConnect'))}</button>`
          : ''}
      </div>
      <div class="cd-vb-body">
        <p class="cd-vb-hint">${escapeHtml(t('onboarding.email.imapSubtitle'))}</p>
        ${rows}
        ${form}
      </div>
    </div>`;
}

// ── WhatsApp-Karte ────────────────────────────────────────

function whatsappCard(ctx) {
  const { s, can } = ctx;
  const list = s.vbInstances || [];
  const canWrite = can('connections:write');
  const canDelete = can('connections:delete');

  const rows = list.length
    ? list
        .map((i) => {
          const st = waState(i);
          const phone = i.phoneNumber ? '+' + String(i.phoneNumber) : '';
          const name = i.label || phone || t('settings.connections.waNoName');
          return `
      <div class="cd-vb-row">
        <div class="cd-vb-main">
          <div class="cd-vb-name">${escapeHtml(name)}</div>
          <div class="cd-vb-sub">${escapeHtml(phone && i.label ? phone : phone ? t('settings.connections.waNumberLabel') : t('settings.connections.waNoNumber'))}</div>
        </div>
        ${badge(st)}
        ${canWrite && st !== 'connected'
            ? `<button class="cd-btn-ghost" data-action="vb-wa-connect" data-id="${escapeHtml(i.id)}">${icon('qr_code_2', 15)}${escapeHtml(t('settings.connections.waConnect'))}</button>`
            : ''}
        ${canWrite && st === 'connected'
            ? `<button class="cd-btn-ghost" data-action="vb-wa-disconnect" data-id="${escapeHtml(i.id)}">${icon('link_off', 15)}${escapeHtml(t('settings.connections.disconnect'))}</button>`
            : ''}
        ${canDelete
            ? `<button class="cd-btn-ghost cd-vb-danger" data-action="vb-wa-delete-data" data-id="${escapeHtml(i.id)}">${icon('delete_sweep', 15)}${escapeHtml(t('settings.connections.waDeleteData'))}</button>`
            : ''}
        ${canDelete
            ? `<button class="cd-btn-ghost cd-vb-danger" data-action="vb-wa-remove" data-id="${escapeHtml(i.id)}">${icon('delete', 15)}${escapeHtml(t('common.delete'))}</button>`
            : ''}
      </div>`;
        })
        .join('')
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.connections.waEmpty'))}</div>`;

  return `
    <div class="cd-emp-card">
      <div class="cd-emp-card-head">${icon('chat', 18)}<h3>WhatsApp</h3>
        ${canWrite
          ? `<button class="cd-btn-ghost" data-action="vb-wa-add" ${s.vbBusy ? 'disabled' : ''}>${icon('add', 15)}${escapeHtml(t('settings.connections.waAddNumber'))}</button>`
          : ''}
      </div>
      <div class="cd-vb-body">
        <p class="cd-vb-hint">${escapeHtml(t('settings.connections.waHint'))}</p>
        ${rows}
      </div>
    </div>`;
}

// ── Abschnitt ─────────────────────────────────────────────

export function renderConnectionsSection(ctx) {
  const { s, can } = ctx;
  if (!can('connections:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.vbLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  return `
    <div class="cd-emp">
      ${s.vbError ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(s.vbError)}</span></div>` : ''}
      ${gmailCard(ctx)}
      ${microsoftCard(ctx)}
      ${imapCard(ctx)}
      ${WHATSAPP_ENABLED ? whatsappCard(ctx) : ''}
    </div>`;
}

// ── QR-Modal (Koppeln) ────────────────────────────────────

export function renderConnectionsModal(ctx) {
  const { s } = ctx;
  const m = s.vbQrModal;
  if (!m) return '';

  const body = m.loading
    ? `<div class="cd-emp-empty">${escapeHtml(t('settings.connections.qrGenerating'))}</div>`
    : m.error
      ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(m.error)}</span></div>`
      : m.connected
        ? `<div class="cd-vb-qr-ok">${icon('check_circle', 32)}<span>${escapeHtml(t('settings.connections.waPaired'))}</span></div>`
        : `
      <div class="cd-vb-qr">
        ${m.qr ? `<img src="${escapeHtml(m.qr)}" alt="${escapeHtml(t('settings.connections.qrAlt'))}" width="264" height="264">` : ''}
        <ol class="cd-vb-steps">
          <li>${escapeHtml(t('settings.connections.qrStep1'))}</li>
          <li>${escapeHtml(t('settings.connections.qrStep2'))}</li>
          <li>${escapeHtml(t('settings.connections.qrStep3'))}</li>
        </ol>
        ${m.pairing ? `<div class="cd-vb-pairing">${escapeHtml(t('settings.connections.qrPairingLabel'))} <code>${escapeHtml(m.pairing)}</code></div>` : ''}
      </div>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="vb-qr-close">
      <div class="cd-emp-modal cd-vb-modal" data-action="vb-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('qr_code_2', 20)}${escapeHtml(t('settings.connections.qrModalTitle'))}</h2>
          <button class="cd-emp-modal-x" data-action="vb-qr-close">${icon('close', 18)}</button>
        </div>
        <div class="cd-emp-modal-body">${body}</div>
      </div>
    </div>`;
}

// ── QR-Kopplung ───────────────────────────────────────────

// Der Timer lebt ausserhalb von `s`: er darf einen redraw überleben, muss aber
// beim Schliessen sterben — sonst pollt er ewig weiter.
let qrPoll = null;

function stopQrPoll() {
  if (qrPoll) { clearInterval(qrPoll); qrPoll = null; }
}

async function openQr(instanceId, ctx) {
  const { s, draw } = ctx;
  stopQrPoll();
  s.vbQrModal = { instanceId, loading: true, qr: '', pairing: '', error: '', connected: false };
  draw();

  try {
    const res = await connectWhatsApp(instanceId);
    const m = s.vbQrModal;
    if (!m || m.instanceId !== instanceId) return; // zwischenzeitlich geschlossen
    m.loading = false;
    m.qr = res.qr || res.qrcode || '';
    m.pairing = res.pairingCode || res.pairing || '';
    if (!m.qr && !m.pairing) m.error = t('settings.connections.qrNoCode');
    draw();
  } catch (err) {
    if (s.vbQrModal) { s.vbQrModal.loading = false; s.vbQrModal.error = err.message || t('settings.connections.pairFailed'); }
    draw();
    return;
  }

  // Bis zum Scannen pollen — WhatsApp meldet uns nicht, wann der Nutzer fertig ist.
  qrPoll = setInterval(async () => {
    const m = s.vbQrModal;
    if (!m || m.instanceId !== instanceId) { stopQrPoll(); return; }
    let st;
    try { st = await getWhatsAppStatus(instanceId); } catch { return; } // Netz-Aussetzer → weiter
    if (st && st.connected === true) {
      stopQrPoll();
      m.connected = true;
      draw();
      await loadConnections(ctx);
      // Kurz den Erfolg zeigen, dann schliessen.
      setTimeout(() => { if (s.vbQrModal && s.vbQrModal.instanceId === instanceId) { s.vbQrModal = null; draw(); } }, 1200);
    }
  }, 2000);
}

// ── Aktionen ──────────────────────────────────────────────

export function handleConnectionsAction(a, el, ctx) {
  const { s, draw, askConfirm } = ctx;
  if (!a || a.indexOf('vb-') !== 0) return false;

  const refresh = () => loadConnections(ctx).then(draw);
  const fail = (err, fallback) => { s.vbError = err.message || fallback; s.vbBusy = false; draw(); };

  if (a === 'vb-qr-close') {
    stopQrPoll();
    s.vbQrModal = null;
    draw();
    return true;
  }

  if (a === 'vb-imap-open') {
    if (!ctx.can('connections:write')) return true;
    s.vbImapOpen = true; s.vbImapError = ''; s.vbImapForm = s.vbImapForm || { security: 'SSL', imapPort: 993, smtpPort: 465 };
    draw();
    return true;
  }

  if (a === 'vb-imap-cancel') {
    s.vbImapOpen = false; s.vbImapError = ''; draw();
    return true;
  }

  if (a === 'vb-imap-save') {
    if (!ctx.can('connections:write')) return true;
    const val = (id) => (document.getElementById(id)?.value || '').trim();
    const form = {
      email: val('vb-imap-email'),
      password: document.getElementById('vb-imap-pass')?.value || '',
      imapHost: val('vb-imap-host'),
      imapPort: Number(val('vb-imap-port')) || 993,
      smtpHost: val('vb-smtp-host'),
      smtpPort: Number(val('vb-smtp-port')) || 465,
      security: val('vb-imap-sec') === 'STARTTLS' ? 'STARTTLS' : 'SSL',
    };
    // Das Passwort bleibt NUR im Request — es wandert nie in den Zustand und
    // damit auch nie in ein erneutes Rendern des Formulars.
    s.vbImapForm = { ...form, password: undefined };
    if (!form.email || !form.password || !form.imapHost || !form.smtpHost) {
      s.vbImapError = t('onboarding.email.connectFailed'); draw(); return true;
    }
    s.vbImapBusy = true; s.vbImapError = ''; draw();
    connectImap(form)
      .then(() => { s.vbImapBusy = false; s.vbImapOpen = false; s.vbImapForm = null; return refresh(); })
      .catch((err) => { s.vbImapBusy = false; s.vbImapError = err.message || t('onboarding.email.connectFailed'); draw(); });
    return true;
  }

  if (a === 'vb-imap-remove') {
    if (!ctx.can('connections:delete')) return true;
    const id = el.dataset.id;
    const conn = (s.vbConnections || []).find((c) => c.id === id);
    askConfirm({
      title: t('settings.connections.gmailDisconnectTitle'),
      message: t('settings.connections.gmailDisconnectMessage', { account: conn ? conn.email : '' }),
      confirmLabel: t('settings.connections.disconnect'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await deleteConnection(id); await refresh(); }
        catch (err) { s.vbError = err.message || t('common.saveFailed'); draw(); }
      },
    });
    return true;
  }

  if (a === 'vb-ms-connect') {
    if (!ctx.can('connections:write')) return true;
    s.vbBusy = true; s.vbError = ''; draw();
    // Gleicher Weg wie bei Google: Popup + Polling (core/oauth.js).
    startOAuthFlow('microsoft', '/api/auth/microsoft/start')
      .then(() => { s.vbBusy = false; return refresh(); })
      .catch((err) => fail(err, t('settings.connections.gmailConnectFailed')));
    return true;
  }

  if (a === 'vb-ms-remove') {
    if (!ctx.can('connections:delete')) return true;
    const id = el.dataset.id;
    const conn = (s.vbConnections || []).find((c) => c.id === id);
    askConfirm({
      title: t('settings.connections.gmailDisconnectTitle'),
      message: t('settings.connections.msDisconnectMessage', { account: conn ? conn.email : t('settings.connections.gmailDisconnectFallbackAccount') }),
      confirmLabel: t('settings.connections.disconnect'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await deleteConnection(id); await refresh(); }
        catch (err) { fail(err, t('settings.connections.disconnectFailed')); }
      },
    });
    return true;
  }

  if (a === 'vb-gmail-connect') {
    if (!ctx.can('connections:write')) return true;
    s.vbBusy = true; s.vbError = ''; draw();
    // Popup + Polling (COOP blockt postMessage) — siehe core/oauth.js
    startOAuthFlow('google', '/api/auth/google/start')
      .then(() => { s.vbBusy = false; return refresh(); })
      .catch((err) => fail(err, t('settings.connections.gmailConnectFailed')));
    return true;
  }

  if (a === 'vb-gmail-remove') {
    if (!ctx.can('connections:delete')) return true;
    const id = el.dataset.id;
    const conn = (s.vbConnections || []).find((c) => c.id === id);
    askConfirm({
      title: t('settings.connections.gmailDisconnectTitle'),
      message: t('settings.connections.gmailDisconnectMessage', { account: conn ? conn.email : t('settings.connections.gmailDisconnectFallbackAccount') }),
      confirmLabel: t('settings.connections.disconnect'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await deleteConnection(id); await refresh(); }
        catch (err) { fail(err, t('settings.connections.disconnectFailed')); }
      },
    });
    return true;
  }

  if (a === 'vb-gmail-delete-data') {
    if (!ctx.can('connections:delete')) return true;
    const id = el.dataset.id;
    const conn = (s.vbConnections || []).find((c) => c.id === id);
    askConfirm({
      title: t('settings.connections.gmailDeleteDataTitle'),
      message: t('settings.connections.gmailDeleteDataMessage', { account: conn ? conn.email : t('settings.connections.gmailDisconnectFallbackAccount') }),
      confirmLabel: t('settings.connections.gmailDeleteData'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await deleteConnectionData(id); await refresh(); }
        catch (err) { fail(err, t('settings.connections.gmailDeleteDataFailed')); }
      },
    });
    return true;
  }

  if (a === 'vb-wa-add') {
    if (!ctx.can('connections:write')) return true;
    s.vbBusy = true; s.vbError = ''; draw();
    createWhatsAppInstance(null)
      .then(async (res) => {
        s.vbBusy = false;
        await refresh();
        // Eine Nummer ohne Kopplung ist nutzlos → direkt den QR zeigen.
        if (res && res.instance && res.instance.id) await openQr(res.instance.id, ctx);
      })
      .catch((err) => fail(err, t('settings.connections.waCreateFailed')));
    return true;
  }

  if (a === 'vb-wa-connect') {
    if (!ctx.can('connections:write')) return true;
    openQr(el.dataset.id, ctx);
    return true;
  }

  if (a === 'vb-wa-disconnect') {
    if (!ctx.can('connections:write')) return true;
    const id = el.dataset.id;
    askConfirm({
      title: t('settings.connections.waDisconnectTitle'),
      message: t('settings.connections.waDisconnectMessage'),
      confirmLabel: t('settings.connections.disconnect'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await disconnectWhatsApp(id); await refresh(); }
        catch (err) { fail(err, t('settings.connections.disconnectFailed')); }
      },
    });
    return true;
  }

  // WhatsApp-Daten löschen — Nummer bleibt verbunden (Gegenstück zum Gmail-Purge)
  if (a === 'vb-wa-delete-data') {
    if (!ctx.can('connections:delete')) return true;
    const id = el.dataset.id;
    const inst = (s.vbInstances || []).find((i) => i.id === id);
    askConfirm({
      title: t('settings.connections.waDeleteDataTitle'),
      message: t('settings.connections.waDeleteDataMessage', {
        number: inst?.label || (inst?.phoneNumber ? '+' + inst.phoneNumber : t('settings.connections.waNoName')),
      }),
      confirmLabel: t('settings.connections.waDeleteData'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await deleteWhatsAppData(id); await refresh(); }
        catch (err) { fail(err, t('settings.connections.waDeleteDataFailed')); }
      },
    });
    return true;
  }

  if (a === 'vb-wa-remove') {
    if (!ctx.can('connections:delete')) return true;
    const id = el.dataset.id;
    askConfirm({
      title: t('settings.connections.waRemoveTitle'),
      message: t('settings.connections.waRemoveMessage'),
      confirmLabel: t('common.delete'),
      danger: true,
      onConfirm: async () => {
        s.vbError = ''; draw();
        try { await removeWhatsAppInstance(id); await refresh(); }
        catch (err) { fail(err, t('common.deleteFailed')); }
      },
    });
    return true;
  }

  return false;
}
